import { PenNibIcon, RotateIcon } from '#icons'
import { useSignatureCapture } from './useSignatureCapture'
import type { WaiverSignaturePaths } from './types'

/**
 * SignaturePad — draw-to-sign surface that emits SVG path data.
 *
 * Renders an inline `<svg>` rather than a `<canvas>`. The strokes it draws
 * *are* the value it emits, so there is no `toDataURL` round-trip, the result
 * is resolution-independent, and the same markup renders in Storybook, in a
 * PDF, and back in the admin drawer.
 *
 * Keyboard users cannot draw — that is inherent to the control, not an
 * oversight. Pair it with a waiver whose `requireSignature` can be turned off
 * when a club needs a checkbox-only path.
 *
 * @example
 * ```tsx
 * <SignaturePad value={signature} onChange={setSignature} />
 * ```
 */
export interface SignaturePadProps {
  value: WaiverSignaturePaths | null
  onChange: (value: WaiverSignaturePaths | null) => void
  /** Fired after the surface is cleared, in addition to `onChange(null)`. */
  onClear?: () => void
  disabled?: boolean
  /** Visible label above the surface. Pass `null` to render none. */
  label?: string | null
  /** Surface height in pixels. @default 180 */
  height?: number
  /** Narrow-screen treatment: clear collapses to a circular icon button. */
  compact?: boolean
  className?: string
  'data-testid'?: string
}

export function SignaturePad({
  value,
  onChange,
  onClear,
  disabled = false,
  label = 'Signature',
  height = 180,
  compact = false,
  className = '',
  'data-testid': dataTestId,
}: SignaturePadProps) {
  const { surfaceRef, viewBox, committed, activePath, isEmpty, handlers, clear } = useSignatureCapture(
    value,
    onChange,
    disabled,
  )

  const handleClear = () => {
    clear()
    onClear?.()
  }

  return (
    <div className={className} data-testid={dataTestId}>
      {(label || !disabled) && (
        <div className="mb-2 flex items-center justify-between">
          {label ? (
            <span className="text-base font-medium text-butter-gray-900">{label}</span>
          ) : (
            <span />
          )}
          {!disabled && (
            <button
              type="button"
              onClick={handleClear}
              disabled={isEmpty}
              aria-label="Clear signature"
              className={
                compact
                  ? 'flex h-9 w-9 items-center justify-center rounded-full bg-butter-gray-50 text-butter-gray-500 text-interactive:bg-butter-gray-100 disabled:cursor-not-allowed disabled:opacity-50'
                  : 'flex items-center gap-1 text-sm text-butter-gray-500 text-interactive:text-butter-gray-700 disabled:cursor-not-allowed disabled:opacity-50'
              }
              data-testid={dataTestId ? `${dataTestId}-clear` : undefined}
            >
              <RotateIcon className="h-4 w-4" />
              {!compact && 'Clear'}
            </button>
          )}
        </div>
      )}

      <div className="relative">
        <svg
          ref={surfaceRef}
          role="application"
          aria-label={label ? `${label} — draw to sign` : 'Draw to sign'}
          aria-disabled={disabled || undefined}
          // Set only once a stroke exists — an empty pad has no geometry to
          // frame, and user space is px space until it does. With it, a
          // signature drawn at 560px and shown in a 320px pad scales instead of
          // being clipped; `toLocalPoint` inverts the same fit so strokes added
          // afterwards still land under the finger.
          {...(viewBox ? { viewBox: viewBox.join(' '), preserveAspectRatio: 'xMidYMid meet' } : {})}
          data-empty={isEmpty || undefined}
          data-disabled={disabled || undefined}
          style={{ height }}
          // `touch-action: none` is load-bearing, not cosmetic — mobile-app sets
          // `touch-action: pan-y` on html/body, so without this the page scrolls
          // instead of the finger drawing.
          // The surface stays grey once signed — the design treats it as a
          // filled field throughout, and only the border switches from the
          // dashed "draw here" invitation to a solid edge.
          className="w-full touch-none select-none rounded-2xl border border-butter-gray-200 bg-butter-gray-50 text-butter-gray-900 data-[empty]:border-dashed data-[disabled]:cursor-not-allowed data-[disabled]:opacity-60"
          {...(disabled ? {} : handlers)}
        >
          {[...committed, ...(activePath ? [activePath] : [])].map((d, index) => (
            <path
              // Strokes are append-only within a signature and never reordered,
              // so the index is stable for the life of the drawing.
              key={index}
              d={d}
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ))}
        </svg>

        {isEmpty && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <PenNibIcon className="h-12 w-12 text-butter-gray-300" />
          </div>
        )}
      </div>

      {/* Announces capture to assistive tech, which cannot observe the drawing. */}
      <span aria-live="polite" className="sr-only">
        {isEmpty ? 'No signature captured' : 'Signature captured'}
      </span>
    </div>
  )
}
